// frontend/src/components/AdCreativeTools/LicenseModal.jsx

import React, { useState, useEffect, useMemo, useContext } from 'react';
import { useTranslation, Trans } from 'react-i18next';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    TextField,
    Typography,
    Box,
    Divider,
    Alert,
    CircularProgress,
    Accordion,
    AccordionSummary,
    AccordionDetails,
    Tooltip,
    IconButton,
    Skeleton,
    Select,
    MenuItem
} from '@mui/material';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import InfoIcon from '@mui/icons-material/Info';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { UIContext } from '../../contexts/UIContext'; 

// --- SYMBOL MAPPING ---

const CURRENCY_SYMBOLS = {
    'USD': '$',
    'EUR': '€',
    'GBP': '£',
    'JPY': '¥',
    'AUD': 'A$',
    'CAD': 'C$',
    'CHF': 'Fr',
    'CNH': '¥'
};

// Helper to format date to YYYY-MM-DD for input values
const formatDate = (date) => date.toISOString().split('T')[0];

// --- SKELETON COMPONENT ---
const LicenseSkeleton = () => (
    <Box sx={{ width: '100%' }}>
        {/* Alert Box mimic */}
        <Skeleton variant="rectangular" animation="wave" height={50} sx={{ borderRadius: 1, mb: 3, bgcolor: 'rgba(0, 0, 0, 0.08)' }} />
        
        {/* Accordion Header mimic */}
        <Skeleton variant="rectangular" animation="wave" height={48} sx={{ borderRadius: 1, mb: 2, bgcolor: 'rgba(0, 0, 0, 0.08)' }} />
        
        {/* Inputs Row mimic */}
        <Box sx={{ display: 'flex', gap: 2, mb: 3, mt: 2, px: 2 }}>
            <Box sx={{ flex: 1 }}>
                <Skeleton variant="text" animation="wave" width="40%" sx={{ mb: 0.5, bgcolor: 'rgba(0, 0, 0, 0.08)' }} />
                <Skeleton variant="rectangular" animation="wave" height={40} sx={{ borderRadius: 1, bgcolor: 'rgba(0, 0, 0, 0.08)' }} />
            </Box>
            <Box sx={{ flex: 1 }}>
                <Skeleton variant="text" animation="wave" width="40%" sx={{ mb: 0.5, bgcolor: 'rgba(0, 0, 0, 0.08)' }} />
                <Skeleton variant="rectangular" animation="wave" height={40} sx={{ borderRadius: 1, bgcolor: 'rgba(0, 0, 0, 0.08)' }} />
            </Box>
        </Box>

        {/* Cost Breakdown mimic */}
        <Box sx={{ px: 2 }}>
            <Skeleton variant="rectangular" animation="wave" height={120} sx={{ borderRadius: 2, bgcolor: 'rgba(0, 0, 0, 0.08)' }} />
        </Box>
    </Box>
);


const LicenseModal = ({ 
    isOpen, 
    onClose, 
    onConfirm,
    onDownload,
    isPaying, 
    isLoading, 
    currentLicense, 
    contentType, 
    pricingConfig, 
    currencyRates, 
    selectedCurrency,
    mode = 'download' // 'download' or 'activate'
}) => {
    const { t } = useTranslation();
    const { openDocViewer } = useContext(UIContext);

    // --- PRICING CONSTANTS ---
    const BASE_FEE = pricingConfig?.prices?.content_license?.[`${contentType}_base_fee`] || 2000;
    const DAILY_FEE = pricingConfig?.prices?.content_license?.[`${contentType}_daily_fee`] || 100;
    const CURRENCY_RATIO = pricingConfig?.billing_meta?.unit_to_currency_ratio || 0.001;

    // --- STATE ---
    const [startDate, setStartDate] = useState('');
    const [endDate, setEndDate] = useState('');
    // Initialize based on props if available to reduce flicker, default to true
    const [isExpanded, setIsExpanded] = useState(() => {
        if (currentLicense && currentLicense.status === 'active') return false;
        return true;
    });
    const [viewCurrency, setViewCurrency] = useState(selectedCurrency || 'USD');
    
    // --- DERIVED STATE ---
    const today = useMemo(() => new Date(), []);
    const todayStr = useMemo(() => formatDate(today), [today]);

    // 1. Calculate if physically expired based on date comparison
    // Use useMemo to prevent recalculation on every render
    const isLicenseExpired = useMemo(() => {
        if (!currentLicense || !currentLicense.expires_at) return false;
        
        const expiryDate = new Date(currentLicense.expires_at);
        // Compare timestamps to be precise (Now vs Expiry)
        return today.getTime() > expiryDate.getTime();
    }, [currentLicense, today]);

    // 2. isActive is only true if DB says active AND it hasn't physically expired
    const isActive = currentLicense && currentLicense.status === 'active' && !isLicenseExpired;

    // Dynamic Button Text Logic
    const getButtonText = () => {
        if (isPaying) return t('licenseModal.buttons.processing');

        // Case 1: Active License + No Time Added = Download
        if (isActive && calculation?.isDownloadOnly) {
             return t('licenseModal.buttons.download');
        }

        // Case 2: Active License + Time Added = Pay & Extend
        if (isActive) {
            return t('licenseModal.buttons.payAndExtend');
        }
        
        // Case 3: New License (Inactive)
        // If mode passed via props was 'download' (e.g. from "Generate Output"), say Pay & Download
        // Else (e.g. from "My Licenses"), say Activate
        if (mode === 'download') return t('licenseModal.buttons.payAndDownload');
        
        return t('licenseModal.buttons.activate');
    };

    // Tooltip Logic
    const getButtonTooltip = () => {
        if (isLoading) return t('licenseModal.buttons.loadingTooltip');
        
        // If Active but not expanded (or invalid selection), guide the user
        if (isActive && (!isExpanded || !calculation)) {
            return t('licenseModal.buttons.selectExtensionPeriodTooltip');
        }
        
        return "";
    };

    // Update local currency if parent prop changes (e.g. re-opening)
    useEffect(() => {
        if(selectedCurrency) setViewCurrency(selectedCurrency);
    }, [selectedCurrency]);

    // Initialize dates, currency, and accordion state
    useEffect(() => {
        if (isOpen && !isLoading) {
            // Always reset to user's preferred currency when modal opens
            if (selectedCurrency) {
                setViewCurrency(selectedCurrency);
            }
            let initialStart = today;
            
            if (isActive) {
                // If active, collapse accordion by default
                setIsExpanded(false);
                // Start date for extension is expiry + 1 day
                const currentEnd = new Date(currentLicense.expires_at);
                initialStart = new Date(currentEnd);

            } else {
                // INACTIVE OR EXPIRED: Start from Today
                // This ensures an expired license resets the extension window to now
                setIsExpanded(true);
                initialStart = today; 
            }

            const initialStartStr = formatDate(initialStart);
            setStartDate(initialStartStr);

            const initialEnd = new Date(initialStart);
            // Use +30 for extensions (active) and +29 for new activations (inactive)
            // This ensures both result in exactly 30 billing units due to the diff calculation logic.
            const daysToAdd = isActive ? 30 : 29;
            initialEnd.setDate(initialEnd.getDate() + daysToAdd);
            setEndDate(formatDate(initialEnd));
        }
    }, [isOpen, isLoading, currentLicense, isActive, selectedCurrency, today]);

    // --- CALCULATIONS ---
    const calculation = useMemo(() => {
        // Dynamic Rate based on switcher
        const currentRate = currencyRates[viewCurrency] || 1;
        // 1. If Active AND (Collapsed OR Dates match), cost is 0, but valid for download
        if (isActive && (!isExpanded || (startDate === endDate))) {
            return {
                days: 0,
                totalUnits: 0,
                totalCostDisplay: new Intl.NumberFormat('en-US', { style: 'currency', currency: viewCurrency }).format(0),
                isExtension: false,
                isDownloadOnly: true
            };
        }

        if (!startDate || !endDate) return null;

        const start = new Date(startDate);
        const end = new Date(endDate);
        const diffTime = end - start; // Raw difference in milliseconds
        
        // Calculate the raw difference in days (e.g., 13th to 14th = 1 day)
        const rawDayDiff = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

        // LOGIC:
        // New Activation: Inclusive (Today to Tomorrow = 2 days) -> rawDayDiff + 1
        // Extension: Exclusive of start (12th to 13th = 1 day added) -> rawDayDiff
        const diffDays = isActive ? rawDayDiff : rawDayDiff + 1;

        // Validation for NEW licenses (must be > 0 days)
        if (!isActive && diffDays <= 0) return null;

        // Validation for EXTENSIONS (allow 0 days -> becomes Download Only)
        if (isActive && diffDays <= 0) {
             return {
                days: 0,
                totalUnits: 0,
                totalCostDisplay: new Intl.NumberFormat('en-US', { style: 'currency', currency: viewCurrency }).format(0),
                isExtension: false,
                isDownloadOnly: true
            };
        }

        // --- FIX: Logic to determine if Base Fee has been paid ---
        // Just checking `!!currentLicense` is incorrect because it might be { status: 'inactive' }.
        // We only consider it paid if status is active or expired.
        const hasPaidBase = currentLicense && (currentLicense.status === 'active' || currentLicense.status === 'expired');
        const applicableBaseFee = hasPaidBase ? 0 : BASE_FEE;
        const totalDailyFee = diffDays * DAILY_FEE;
        const totalUnits = applicableBaseFee + totalDailyFee;
        const totalCost = totalUnits * CURRENCY_RATIO * currentRate;

        return {
            days: diffDays,
            hasPaidBase,
            baseFeeUnits: applicableBaseFee,
            dailyFeeUnits: totalDailyFee,
            totalUnits,
            totalCostDisplay: new Intl.NumberFormat('en-US', { style: 'currency', currency: viewCurrency }).format(totalCost),
            isExtension: true,
            isDownloadOnly: false
        };
    }, [startDate, endDate, currentLicense, isActive, isExpanded, BASE_FEE, DAILY_FEE, CURRENCY_RATIO, currencyRates, viewCurrency]);

    const handleConfirm = () => {
        if (!calculation) return;

        // Path A: Zero Cost / Download Only
        if (calculation.isDownloadOnly) {
            if (onDownload) {
                onDownload(); // Call parent download function
            }
            onClose(); // Close modal
            return;
        }

        // Path B: Payment Required
        onConfirm(calculation.days, calculation.totalUnits);
    };

    // --- Dynamic Status Message ---
    const renderStatusAlert = () => {
        if (isActive) {
            // Note: currentLicense.expires_at comes from DB as ISO string (e.g., 2026-02-12T23:59:59...)
            const currentExpiryDate = new Date(currentLicense.expires_at);
            
            // Force UTC timezone so 23:59 UTC doesn't roll over to the next day in local time
            const currentExpiryStr = currentExpiryDate.toLocaleDateString(undefined, { timeZone: 'UTC' });
            
            if (isExpanded && calculation?.isExtension) {
                // For the "Extending until..." part, we use the user-selected 'endDate' string directly.
                // This avoids timezone shifting issues since the input is YYYY-MM-DD.
                // We create a date object from it just for locale formatting, treating it as UTC to stay safe,
                // or just parsing the YYYY-MM-DD parts to be explicit.
                
                // Simple parsing to avoid timezone shift on the display:
                const [y, m, d] = endDate.split('-').map(Number);
                const extensionDate = new Date(y, m - 1, d).toLocaleDateString();

                return (
                    <Alert 
                        icon={<CheckCircleOutlineIcon fontSize="inherit" />} 
                        severity="success" 
                        sx={{ mb: 3, alignItems: 'center', backgroundColor: '#e8f5e9', color: '#2e7d32', '& .MuiAlert-icon': { color: '#2e7d32' } }}
                    >
                        <Trans 
                            i18nKey="licenseModal.alerts.activeExtension"
                            values={{ currentDate: currentExpiryStr, newDate: extensionDate }}
                            components={{ strong: <strong /> }}
                        />
                    </Alert>
                );
            }
            // Standard Info Alert (Also shown when extending 0 days/download only)
            return (
                <Alert severity="info" sx={{ mb: 3, alignItems: 'center' }}>
                    {t('licenseModal.alerts.activeInfo', { currentDate: currentExpiryStr })}
                </Alert>
            );
        }
        
        if (currentLicense && currentLicense.status === 'expired') {
             // Expired
             return (
                <Alert severity="warning" sx={{ mb: 3, alignItems: 'center' }}>
                    {t('licenseModal.alerts.expired')}
                </Alert>
            );
        }

        // New
        return (
            <Alert severity="warning" sx={{ mb: 3, alignItems: 'center' }}>
                {t('licenseModal.alerts.new')}
            </Alert>
        );
    };

    return (
        <Dialog 
            open={isOpen} 
            onClose={(event, reason) => {
                if (reason !== 'backdropClick' && !isPaying) {
                    onClose();
                }
            }}
            BackdropProps={{
                sx: {
                    backdropFilter: 'blur(5px)',
                    WebkitBackdropFilter: 'blur(5px)', // Safari support
                    backgroundColor: 'rgba(0, 0, 0, 0.5)' // Standard dimming
                }
            }}
            PaperProps={{
                sx: {
                    backgroundColor: '#ffffff', // FORCE WHITE BACKGROUND
                    color: 'rgba(0, 0, 0, 0.87)', // DARK TEXT
                    minWidth: '550px',
                    borderRadius: '12px',
                    boxShadow: '0 8px 32px rgba(0, 0, 0, 0.5)'
                }
            }}
        >
            <DialogTitle sx={{ 
                borderBottom: '1px solid rgba(255, 255, 255, 0.12)',
                display: 'flex', 
                alignItems: 'center', 
                justifyContent: 'space-between', // Push help icon to right
                color: 'rgba(0, 0, 0, 0.87)',
                padding: '16px 24px'
            }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <span style={{ fontWeight: 600, color: '#ffffff' }}>{t('licenseModal.title')}</span>
                </Box>
                <Tooltip 
                    title={t('licenseModal.helpTooltip')}
                    arrow
                    placement="top"
                    PopperProps={{ className: 'global-dark-tooltip' }}
                >
                    <IconButton 
                        size="small" 
                        sx={{ 
                            color: 'var(--secondary-color)', 
                            backgroundColor: 'rgba(79, 195, 247, 0.1)', 
                            transition: 'all 0.2s ease-in-out', // Smooth transition
                            '&:hover': {
                                backgroundColor: 'rgba(79, 195, 247, 0.2)', // Darker bg on hover
                                borderColor: 'var(--secondary-color)', // Solid border on hover
                            }
                        }}
                        onClick={() => openDocViewer('business/billing_and_usage_units.md')}
                    >
                        <HelpOutlineIcon fontSize="small" />
                    </IconButton>
                </Tooltip>
            </DialogTitle>
            
            <DialogContent sx={{ mt: 2, padding: '24px', minHeight: '300px', display: 'flex', flexDirection: 'column', position: 'relative' }}>
                
                {/* LOADING OVERLAY + SKELETON */}
                {isLoading && (
                    <>
                        {/* Overlay to dim the skeleton and center the spinner */}
                        <Box sx={{
                            position: 'absolute',
                            top: 0,
                            left: 0,
                            width: '100%',
                            height: '100%',
                            zIndex: 10,
                            // backgroundColor: 'rgba(255, 255, 255, 0.7)',
                            display: 'flex',
                            flexDirection: 'column',
                            justifyContent: 'center',
                            alignItems: 'center',
                            gap: 2
                        }}>
                            <CircularProgress size={48} sx={{ color: 'var(--secondary-color)' }} />
                            <Typography variant="body1" sx={{ color: '#ffffff', fontWeight: 600 }}>
                                {t('licenseModal.loading')}
                            </Typography>
                        </Box>
                        {/* The Skeleton Background */}
                        <LicenseSkeleton />
                    </>
                )}

                {/* REAL CONTENT (Hidden when loading, or conditionally rendered) */}
                {!isLoading && (
                    <>
                        {renderStatusAlert()}

                        {/* Accordion for Extension / Payment */}
                        <Accordion 
                            expanded={isExpanded} 
                            onChange={(e, expanded) => {
                                // If inactive, prevent collapsing (Must pay to download)
                                if (!isActive) return;
                                setIsExpanded(expanded);
                            }}
                            disableGutters
                            elevation={0}
                            sx={{
                                border: '1px solid #e0e0e0',
                                borderRadius: '8px !important',
                                '&:before': { display: 'none' },
                                mb: 2,
                                boxShadow: 'none'
                            }}
                        >
                            <AccordionSummary 
                                expandIcon={isActive && <ExpandMoreIcon />}
                                sx={{ backgroundColor: '#f9fafb', borderRadius: '8px' }}
                            >
                                <Typography sx={{ fontWeight: 600, color: 'rgba(0,0,0,0.7)' }}>
                                    {isActive ? t('licenseModal.accordion.extend') : t('licenseModal.accordion.select')}
                                </Typography>
                            </AccordionSummary>
                            <AccordionDetails sx={{ p: 2 }}>
                                <Box sx={{ display: 'flex', gap: 2, mb: 3 }}>
                                    {/* Start Date */}
                                    <TextField
                                        label={
                                            <Box component="span" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                                {t('licenseModal.dates.startLabel')}
                                                <Tooltip 
                                                    title={isActive ? t('licenseModal.dates.startTooltipExtended') : t('licenseModal.dates.startTooltipNew')}  
                                                    arrow 
                                                    placement="top" 
                                                    PopperProps={{ className: 'global-dark-tooltip' }}
                                                >
                                                    <InfoOutlinedIcon sx={{ fontSize: '0.9rem', color: 'rgba(0,0,0,0.4)', cursor: 'help' }} />
                                                </Tooltip>
                                            </Box>
                                        }
                                        type="date"
                                        value={startDate}
                                        onChange={(e) => setStartDate(e.target.value)}
                                        fullWidth
                                        disabled={isActive} // Locked if extending
                                        InputLabelProps={{ shrink: true }}
                                        inputProps={{ 
                                            min: todayStr,
                                            style: { colorScheme: 'light' } 
                                        }}
                                        size="small"
                                        // Explicit coloring for White Modal context
                                        sx={{
                                            '& .MuiInputLabel-root': { color: 'rgba(0, 0, 0, 0.6) !important' },
                                            '& .MuiInputBase-input': { color: 'rgba(0, 0, 0, 0.87) !important' },
                                            '& .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(0, 0, 0, 0.23) !important' },
                                            '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(0, 0, 0, 0.87) !important' },
                                            '& .Mui-disabled': { 
                                                color: 'rgba(0, 0, 0, 0.38) !important', 
                                                '-webkit-text-fill-color': 'rgba(0, 0, 0, 0.38) !important' 
                                            }
                                        }}
                                    />
                                    {/* End Date - Tooltip moved to label */}
                                    <TextField
                                        label={
                                            <Box component="span" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                                {t('licenseModal.dates.endLabel')}
                                                <Tooltip 
                                                    title={t('licenseModal.dates.endTooltip')}
                                                    arrow 
                                                    placement="top" 
                                                    PopperProps={{ className: 'global-dark-tooltip' }}
                                                >
                                                    <InfoOutlinedIcon sx={{ fontSize: '0.9rem', color: 'rgba(0,0,0,0.4)', cursor: 'help' }} />
                                                </Tooltip>
                                            </Box>
                                        }
                                        type="date"
                                        value={endDate}
                                        onChange={(e) => setEndDate(e.target.value)}
                                        fullWidth
                                        InputLabelProps={{ shrink: true }}
                                        inputProps={{ 
                                            min: startDate || todayStr,
                                            style: { colorScheme: 'light' } 
                                        }}
                                        size="small"
                                        sx={{
                                            '& .MuiInputLabel-root': { color: 'rgba(0, 0, 0, 0.6) !important' },
                                            '& .MuiInputBase-input': { color: 'rgba(0, 0, 0, 0.87) !important' },
                                            '& .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(0, 0, 0, 0.23) !important' },
                                            '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(0, 0, 0, 0.87) !important' },
                                            '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: 'var(--secondary-color) !important' }
                                        }}
                                    />
                                </Box>

                                {/* Cost Breakdown (Only visible inside accordion) */}
                                {calculation && (
                                    <Box sx={{ backgroundColor: '#f5f5f5', p: 2, borderRadius: '8px', border: '1px solid #e0e0e0' }}>
                                        {/* HEADER WITH CURRENCY SWITCHER */}
                                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                                            <Typography variant="subtitle2" sx={{ color: 'rgba(0,0,0,0.6)', textTransform: 'uppercase', fontSize: '0.75rem' }}>
                                                {t('licenseModal.breakdown.header', { days: calculation.days })}
                                            </Typography>

                                            {/* Currency Selector */}
                                            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                               <Tooltip title={t('billingPage.exchangeRateTooltip')} arrow placement="left" PopperProps={{ className: 'global-dark-tooltip' }}>
                                                    <InfoIcon sx={{ fontSize: '0.85rem', color: 'rgba(0,0,0,0.4)', mr: 0.5 }} />
                                                </Tooltip>
                                                <Select
                                                    value={viewCurrency}
                                                    onChange={(e) => setViewCurrency(e.target.value)}
                                                    variant="standard"
                                                    disableUnderline
                                                    sx={{
                                                        fontSize: '0.75rem',
                                                        // Target the inner text element directly
                                                        '& .MuiSelect-select': { 
                                                            color: 'rgba(0,0,0,0.87) !important',
                                                            paddingRight: '16px !important', 
                                                            paddingBottom: '0px' 
                                                        },
                                                        // Target the arrow icon
                                                        '& .MuiSvgIcon-root': { 
                                                            right: '-2px', 
                                                            color: 'rgba(0,0,0,0.54) !important' 
                                                        }
                                                    }}
                                                    MenuProps={{ 
                                                        PaperProps: { sx: { bgcolor: '#fff', color: '#000' } }
                                                    }}
                                                >
                                                    {Object.keys(currencyRates).map(curr => (
                                                        <MenuItem key={curr} value={curr} sx={{ fontSize: '0.8rem' }}>
                                                            {curr} ({CURRENCY_SYMBOLS[curr] || curr})
                                                        </MenuItem>
                                                    ))}
                                                </Select>
                                            </Box>
                                        </Box>
                                        
                                        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '4px', fontSize: '0.9rem', color: 'rgba(0,0,0,0.87)' }}>
                                            <span>{t('licenseModal.breakdown.baseFee')} {calculation.hasPaidBase && <span style={{color: '#2e7d32', fontSize: '0.8em', fontWeight: 'bold'}}>{t('licenseModal.breakdown.paid')}</span>}</span>
                                            <span>{calculation.baseFeeUnits} {t('licenseModal.breakdown.units')}</span>
                                        </div>
                                        
                                        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px', fontSize: '0.9rem', color: 'rgba(0,0,0,0.87)' }}>
                                            <span>{t('licenseModal.breakdown.dailyFee', { fee: DAILY_FEE, days: calculation.days })}</span>
                                            <span>{calculation.dailyFeeUnits} {t('licenseModal.breakdown.units')}</span>
                                        </div>

                                        <Divider sx={{ borderColor: 'rgba(0,0,0,0.12)', my: 1 }} />
                                        
                                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                            <Typography variant="body1" sx={{ fontWeight: 'bold', color: 'rgba(0,0,0,0.87)' }}>{t('licenseModal.breakdown.totalCost')}</Typography>
                                            <Box sx={{ textAlign: 'right' }}>
                                                <Typography variant="h6" sx={{ color: 'var(--secondary-color)', fontWeight: 'bold' }}>
                                                    {calculation.totalCostDisplay}
                                                </Typography>
                                                <Typography variant="caption" sx={{ color: 'rgba(0,0,0,0.6)' }}>
                                                    {calculation.totalUnits}
                                                </Typography>
                                            </Box>
                                        </div>
                                    </Box>
                                )}
                            </AccordionDetails>
                        </Accordion>
                    </>
                )}
            </DialogContent>

            <DialogActions sx={{ p: 2, borderTop: '1px solid rgba(0,0,0,0.1)' }}>
                {/* Cancel is allowed even during loading */}
                <Button onClick={onClose} disabled={isPaying} sx={{ color: 'rgba(255,255,255,0.6)' }}>{t('licenseModal.buttons.cancel')}</Button>
                
                <Tooltip 
                    title={getButtonTooltip()}
                    arrow 
                    placement="top" 
                    PopperProps={{ className: 'global-dark-tooltip' }}
                >
                    <span>
                        <Button 
                            variant="contained" 
                            sx={{ 
                                backgroundColor: (isActive && calculation?.isDownloadOnly) 
                                    ? 'var(--secondary-color)'
                                    : (isPaying ? '#9e9e9e !important' : 'var(--secondary-color)'), // Blue for Pay
                                color: 'white',
                                '&:hover': {
                                     backgroundColor: isPaying ? '#9e9e9e' : 'var(--btn-hover-color)'
                                },
                                '&.Mui-disabled': {
                                    backgroundColor: 'rgba(255, 255, 255, 0.12) !important',
                                    color: 'rgba(255, 255, 255, 0.3) !important'
                                }
                            }}
                            onClick={handleConfirm}
                            disabled={!calculation || (calculation.totalUnits === 0 && !calculation.isDownloadOnly) || isPaying || isLoading}
                            startIcon={
                                isPaying ? (
                                     <CircularProgress size={20} color="inherit" />
                                ) : (<FileDownloadIcon />)
                            }
                        >
                            {getButtonText()}
                        </Button>
                    </span>
                </Tooltip>
            </DialogActions>
        </Dialog>
    );
};

export default LicenseModal;